import React, { FC } from 'react';
import classnames from 'classnames';
import Select from '@src/components/Select';
import { GetOpeningNamesQuery, OpeningStateEnum } from '@src/graphql/generated';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { QueryParams } from '../../interfaces/queryParams.interface';
import { CANDIDATES_FILTER_OPTIONS, FILTER_ALL } from '../../constants/filters';
import { CandidateFilterProps } from './props';
import './styles.scss';

const openingStateToLabel = {
  [OpeningStateEnum.ACTIVE]: 'Active',
  [OpeningStateEnum.ADMIN_REVIEW]: 'In review',
  [OpeningStateEnum.ARCHIVED]: 'Archived',
  [OpeningStateEnum.DELETED]: 'Deleted',
  [OpeningStateEnum.DRAFT]: 'Draft',
  [OpeningStateEnum.INACTIVE]: 'Inactive',
  [OpeningStateEnum.PAUSED]: 'Paused',
  [OpeningStateEnum.SUPPORT]: 'Support',
};

export const CandidateFilter: FC<CandidateFilterProps> = ({
  openingsFilterOptions,
  shouldShowRecruitersList,
  recruiters,
}) => {
  const { queryParams, updateQueryParams } = useQueryParams<QueryParams>();

  const selectedRecruiterOption =
    recruiters.find(recruiter => {
      return recruiter.id === queryParams.selectedRecruiter;
    }) || FILTER_ALL;

  const selectedOpeningOption =
    openingsFilterOptions.find(opening => {
      return opening.id === queryParams.selectedOpening;
    }) || FILTER_ALL;

  return (
    <div className="inbox-candidates-filter">
      <div className="inbox-candidates-filter__select-filters">
        {shouldShowRecruitersList && recruiters && recruiters.length > 1 && (
          <Select
            className="inbox-candidates-filter__recruiter-select"
            onSelect={recruiter => {
              updateQueryParams({ selectedRecruiter: recruiter.id });
            }}
            optionKey="id"
            optionValue={recruiter => {
              return <div>{recruiter.fullName || recruiter.title}</div>;
            }}
            options={recruiters}
            selectedOption={selectedRecruiterOption}
          />
        )}

        {openingsFilterOptions.length > 1 && (
          <Select
            onSelect={opening => {
              if (opening.id === FILTER_ALL.id) {
                updateQueryParams({ selectedOpening: undefined });

                return;
              }

              updateQueryParams({ selectedOpening: opening.id });
            }}
            optionKey="id"
            optionValue={(opening?: GetOpeningNamesQuery['openings']['result'][0]) => {
              const openingStateModifier = opening?.state?.toLowerCase() || '';
              const openingStateLabel = opening?.state ? openingStateToLabel[opening?.state] || '' : '';

              return (
                <div className="inbox-candidates-filter__opening-list-item">
                  <div className="inbox-candidates-filter__opening">
                    <h5>{opening?.title}</h5>

                    <span
                      className={classnames(
                        'inbox-candidates-filter__opening-list-item-status',
                        `inbox-candidates-filter__opening-list-item-status--${openingStateModifier}`,
                      )}
                    >
                      {openingStateLabel}
                    </span>
                  </div>
                </div>
              );
            }}
            options={openingsFilterOptions}
            selectedOption={selectedOpeningOption}
          />
        )}
      </div>

      <div className="inbox-candidates-filter__state-filters">
        <span className="inbox-candidates-filter__section-title">Filter by</span>

        <div className="inbox-candidates-filter__types">
          {CANDIDATES_FILTER_OPTIONS.map(item => {
            return (
              <div
                className={classnames(
                  'inbox-candidates-filter__type',
                  queryParams.selectedCandidatesFilter === item.id && 'inbox-candidates-filter__type--active',
                )}
                key={item.id}
                onClick={() =>
                  updateQueryParams({
                    selectedCandidatesFilter: item.id,
                  })
                }
              >
                <div className="inbox-candidates-filter__label">{item.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
